import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./ComplaintStatus.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? (API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`)
  : "/api";

const STATUS_ALLOWED_MODULES = [1, 3, 7, 13, 15, 16, 19];
const NEW_ALLOWED_MODULES = [1, 3, 7, 13, 16, 19];

function formatDateTime(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";

  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = String(dt.getFullYear()).slice(-2);
  const hours = String(dt.getHours()).padStart(2, "0");
  const mins = String(dt.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${mins}`;
}

function formatDateShort(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";

  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = String(dt.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function resolveAttachmentUrl(pathValue) {
  const raw = String(pathValue || "").trim();
  if (!raw) {
    return "";
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  if (raw.startsWith("/")) {
    return `${API_ROOT}${raw}`;
  }

  return `${API_ROOT}/${raw.replace(/^\/+/, "")}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export default function ComplaintStatus() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const employeeId = Number.parseInt(localUser?.id, 10);
  const deptId = Number.parseInt(localUser?.deptid || localUser?.deptId, 10);
  const moduleId = Number.parseInt(localUser?.moduleId, 10);
  const canCreateNew = NEW_ALLOWED_MODULES.includes(moduleId);

  const loadStatus = useCallback(async () => {
    if (!employeeId || !moduleId) {
      setError("Unauthorized access Or Session Timed out.");
      setLoading(false);
      return;
    }

    if (!STATUS_ALLOWED_MODULES.includes(moduleId)) {
      setError("Unauthorized access Or Session Timed out.");
      setLoading(false);
      return;
    }

    try {
      setError("");
      setLoading(true);

      const query = new URLSearchParams({
        employeeId: String(employeeId),
        moduleId: String(moduleId)
      });

      const response = await fetch(`${API_BASE}/complaints/status?${query.toString()}`);
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.message || "Failed to fetch complaint status");
      }

      setRows(Array.isArray(payload?.data) ? payload.data : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch complaint status");
    } finally {
      setLoading(false);
    }
  }, [employeeId, moduleId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const openPrint = (ticketId) => {
    const query = new URLSearchParams();
    query.set("employeeId", String(employeeId || ""));
    query.set("moduleId", String(moduleId || ""));
    if (deptId) {
      query.set("deptId", String(deptId));
    }

    const url = `${API_BASE}/complaints/print/${encodeURIComponent(ticketId)}?${query.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openAttachment = (docPath) => {
    const resolved = resolveAttachmentUrl(docPath);
    if (!resolved) {
      window.alert("No attachment found.");
      return;
    }

    window.open(resolved, "_blank", "noopener,noreferrer");
  };

  const openProgress = async (ticketId) => {
    const childWindow = window.open(
      "",
      `ticket_progress_${ticketId}`,
      "width=980,height=700,left=80,top=60,resizable=yes,scrollbars=yes"
    );

    if (!childWindow) {
      window.alert("Popup blocked. Please allow popups for this site.");
      return;
    }

    childWindow.document.write("<html><head><title>Progress Details</title></head><body style='font-family:Arial,sans-serif;padding:16px;'><h3>Progress Details</h3><p>Loading...</p></body></html>");
    childWindow.document.close();

    try {
      const query = new URLSearchParams();
      query.set("employeeId", String(employeeId || ""));
      query.set("moduleId", String(moduleId || ""));
      if (deptId) {
        query.set("deptId", String(deptId));
      }

      const response = await fetch(`${API_BASE}/complaints/progress/${encodeURIComponent(ticketId)}?${query.toString()}`);
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.message || "Failed to fetch complaint progress");
      }

      const progressData = Array.isArray(payload?.data) ? payload.data : [];
      const rowsHtml = progressData.length
        ? progressData.map((row) => `
            <tr>
              <td>${escapeHtml(row.ProgressDetails || "-")}</td>
              <td>${escapeHtml(row.Remark || "-")}</td>
              <td>${escapeHtml(formatDateShort(row.DOWP))}</td>
              <td>${escapeHtml(row.StatusLabel || row.Status || "-")}</td>
              <td>${Number(row.EmployeeID) === 36 ? "" : escapeHtml(row.Resolver || "-")}</td>
            </tr>
          `).join("")
        : `<tr><td colspan="5" style="text-align:center;color:#666;">No progress found.</td></tr>`;

      childWindow.document.open();
      childWindow.document.write(`
        <html>
          <head>
            <title>Progress Details</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 16px; }
              h3 { margin: 0 0 12px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #333; padding: 6px; vertical-align: top; }
              thead th { background: #008080; color: white; }
            </style>
          </head>
          <body>
            <h3>Progress Details (Ref.ID ${escapeHtml(ticketId)})</h3>
            <table>
              <thead>
                <tr>
                  <th>Progress Details</th>
                  <th>Remarks</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Transferred To</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </body>
        </html>
      `);
      childWindow.document.close();
    } catch (err) {
      childWindow.document.open();
      childWindow.document.write(`
        <html>
          <head><title>Progress Details</title></head>
          <body style="font-family:Arial,sans-serif;padding:16px;">
            <h3>Progress Details</h3>
            <p style="color:#b00020;">${escapeHtml(err.message || "Failed to fetch complaint progress")}</p>
          </body>
        </html>
      `);
      childWindow.document.close();
    }
  };

  return (
    <section className="container-fluid">
      {canCreateNew && (
        <div style={{ textAlign: "left", marginTop: "10px" }}>
          <Link className="btn btn-primary" to="/residential-complaints/new">
            New Residential Complaint
          </Link>
        </div>
      )}

      <h3 className="mt-3 mb-3">Complaint Status</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-bordered table-striped residential-complaints-table">
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th>SNo</th>
              <th>Type</th>
              <th>Complaint Description</th>
              <th>Remarks</th>
              <th>Area Type</th>
              <th>HNo/Dept</th>
              <th>Dept</th>
              <th>DOC</th>
              <th>Status</th>
              <th>Ref.ID</th>
              <th>AssignedTo</th>
              <th>Print</th>
              <th>Attachment</th>
              <th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={14} className="text-center text-muted">Loading...</td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={14} className="text-center text-muted">No records found.</td>
              </tr>
            )}

            {!loading && rows.map((row, index) => (
              <tr key={`${row.TicketID}-${index}`}>
                <td>{index + 1}</td>
                <td>{row.ComplaintCategory || "-"}</td>
                <td>{row.ComplaintDescription || "-"}</td>
                <td>{row.Description || "-"}</td>
                <td>{row.ResiType || "-"}</td>
                <td>{row.ResidenceID || "-"}</td>
                <td>{row.Department || "-"}</td>
                <td>{formatDateTime(row.DOC)}</td>
                <td>{row.StatusLabel || "-"}</td>
                <td>{row.TicketID || "-"}</td>
                <td>{row.EmpName || "-"}</td>
                <td>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openPrint(row.TicketID)}>
                    Print
                  </button>
                </td>
                <td>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openAttachment(row.SupportingDocUrl)}>
                    Attachment
                  </button>
                </td>
                <td>
                  <button type="button" className="btn btn-sm btn-outline-info" onClick={() => openProgress(row.TicketID)}>
                    Progress
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
